import { useEffect, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Switch } from '@headlessui/react';
import toast from 'react-hot-toast';

import { Dropdown, Input, Button } from '@/shared';
import { HousesErrorBoundary } from '../components';
import { useHouse, useHouseMutations } from '../hooks';
import { houseApi } from '../api';
import {
  availabilityOptions,
  cities,
  currencyOptions,
  furnishedOptions,
  houseTypes,
  sizeUnits,
  states,
} from '../data';

// ===== Validation Schemas per section =====
const basicSchema = yup.object({
  houseName: yup.string().required('House name is required'),
  houseType: yup.string().required('House type is required'),
  bedrooms: yup.number().required().min(1),
  bathrooms: yup.number().required().min(1),
});

const locationSchema = yup.object({
  address: yup.string().required('Address is required'),
  city: yup.string().required('City is required'),
  state: yup.string().required('State is required'),
  zip: yup.string().required('ZIP code is required'),
});

const specsSchema = yup.object({
  area: yup.number().required('Area is required'),
  size: yup.string().required('Size unit is required'),
  furnished: yup.string().required('Furnished status is required'),
  builtYear: yup.number().nullable(),
  parkingAvailable: yup.boolean(),
});

const pricingSchema = yup.object({
  price: yup.number().required('Price is required'),
  currency: yup.string().required('Currency is required'),
  availability: yup.string().required('Availability is required'),
  listingDate: yup.date().required('Listing date is required'),
});

const mediaSchema = yup.object({
  media: yup.mixed().nullable(),
});

export const EditHousePageContent = () => {
  const [activeTab, setActiveTab] = useState<
    'basic' | 'location' | 'specs' | 'pricing' | 'media'
  >('basic');

  const { houseId } = useParams({ from: '/houses/edit/$houseId' });
  const { data, isLoading, error } = useHouse(houseId!);
  const house = data?.house;
  const { updateHouse, isUpdating } = useHouseMutations();

  // Media upload states
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadStatuses, setUploadStatuses] = useState<
    Record<
      string,
      { status: 'Idle' | 'Uploading' | 'Success' | 'Error'; path?: string }
    >
  >({});

  // Separate forms
  const basicForm = useForm({ resolver: yupResolver(basicSchema) });
  const locationForm = useForm({ resolver: yupResolver(locationSchema) });
  const specsForm = useForm({ resolver: yupResolver(specsSchema) });
  const pricingForm = useForm({ resolver: yupResolver(pricingSchema) });
  const mediaForm = useForm({ resolver: yupResolver(mediaSchema) });

  useEffect(() => {
    if (house) {
      basicForm.reset({
        houseName: house.name || '',
        houseType: house.type || '',
        bedrooms: house.bedrooms || 1,
        bathrooms: house.bathrooms || 1,
      });
      locationForm.reset({
        address: house.address || '',
        city: house.city || '',
        state: house.state || '',
        zip: house.zipCode || '',
      });
      specsForm.reset({
        area: house.area || '',
        size: house.size || '',
        furnished: house.isFurnished ? 'Yes' : 'No',
        builtYear: house.builtYear || '',
        parkingAvailable: house.isParkingAvailable || false,
      } as any);
      pricingForm.reset({
        price: house.price?.toString() || '',
        currency: house.currency || '',
        availability: house.status || '',
        listingDate: house.createdAt || '',
      } as any);
      mediaForm.reset({ media: null });
    }
  }, [house]);

  // File selection handler
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const remainingSlots = 10 - selectedFiles.length;
      const newFiles = files.slice(0, remainingSlots);

      if (files.length > remainingSlots) {
        toast.error(
          `You can only upload up to 10 images. Adding first ${remainingSlots} files.`,
        );
      }

      setSelectedFiles((prev) => [...prev, ...newFiles]);

      const newStatuses: Record<
        string,
        { status: 'Idle' | 'Uploading' | 'Success' | 'Error' }
      > = {};
      newFiles.forEach((file) => {
        newStatuses[file.name] = { status: 'Idle' };
      });
      setUploadStatuses((prev) => ({ ...prev, ...newStatuses }));
    }
  };

  // Upload handler
  const handleUpload = async (file: File) => {
    if (!houseId) {
      toast.error('House ID not found');
      return;
    }

    try {
      setUploadStatuses((prev) => ({
        ...prev,
        [file.name]: { status: 'Uploading' },
      }));

      const { signedUrl, path } = await houseApi.getUploadUrl(
        houseId,
        file.name,
        file.type,
      );

      await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      setUploadStatuses((prev) => ({
        ...prev,
        [file.name]: { status: 'Success', path },
      }));

      // Backend stores S3 keys but returns signed URLs when fetching
      // We need to extract S3 keys from the signed URLs before updating
      const extractS3Key = (url: string): string => {
        if (!url.includes('?')) return url; // Already an S3 key
        try {
          const urlObj = new URL(url);
          // Extract path and remove leading slash
          return decodeURIComponent(urlObj.pathname.substring(1));
        } catch {
          return url; // Fallback to original if parsing fails
        }
      };

      const currentS3Keys = (house?.images || []).map(extractS3Key);
      const updatedS3Keys = [...currentS3Keys, path];

      await updateHouse.mutateAsync({
        id: houseId,
        data: { images: updatedS3Keys },
      });

      toast.success(`${file.name} uploaded successfully`);
    } catch (error) {
      console.error(error);
      setUploadStatuses((prev) => ({
        ...prev,
        [file.name]: { status: 'Error' },
      }));
      toast.error(`Failed to upload ${file.name}`);
    }
  };

  // Delete image handler
  const handleDeleteImage = async (imagePath: string) => {
    if (!houseId || !house) return;

    try {
      // Extract S3 key from the image path (might be a signed URL)
      let s3KeyToDelete = imagePath;
      if (imagePath.includes('?')) {
        const url = new URL(imagePath);
        s3KeyToDelete = decodeURIComponent(url.pathname.substring(1));
      }

      // Extract S3 keys from all images and filter out the deleted one
      const updatedImages = (house.images || [])
        .map((img) => {
          if (img.includes('?')) {
            const url = new URL(img);
            return decodeURIComponent(url.pathname.substring(1));
          }
          return img;
        })
        .filter((img) => img !== s3KeyToDelete);

      await updateHouse.mutateAsync({
        id: houseId,
        data: { images: updatedImages },
      });
      toast.success('Image deleted successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete image');
    }
  };

  const handleSave = (values: any, section: string) => {
    if (!houseId) return;

    let updateData: any = {};

    switch (section) {
      case 'Basic':
        updateData = {
          name: values.houseName,
          type: values.houseType,
          bedrooms: Number(values.bedrooms),
          bathrooms: Number(values.bathrooms),
        };
        break;
      case 'Location':
        updateData = {
          address: values.address,
          city: values.city,
          state: values.state,
          zipCode: values.zip,
        };
        break;
      case 'Specifications':
        updateData = {
          area: String(values.area), // Convert to string to match backend schema
          size: values.size,
          isFurnished: values.furnished === 'Yes',
          builtYear: values.builtYear ? String(values.builtYear) : undefined,
          isParkingAvailable: values.parkingAvailable,
        };
        break;
      case 'Pricing':
        updateData = {
          price: Number(values.price),
          currency: values.currency,
          status: values.availability,
          listingDate: values.listingDate,
        };
        break;
      default:
        break;
    }

    updateHouse.mutate({ id: houseId, data: updateData });
  };

  if (isLoading) return <p>Loading...</p>;
  if (error) return <p>Error loading house details.</p>;

  return (
    <div className="min-h-screen bg-background w-full px-0 sm:px-6 lg:px-8 pb-10">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl md:text-3xl font-bold mb-2 text-text-primary">
            Edit House
          </h3>
          <p className="text-gray-600 text-base md:text-xl text-text-secondary">
            House management / Manage houses / Edit houses
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-8 overflow-x-auto">
        <div className="flex min-w-max gap-4 border-b border-gray-200">
          {[
            { key: 'basic', label: 'Basic Information' },
            { key: 'location', label: 'Location Details' },
            { key: 'specs', label: 'House Specifications' },
            { key: 'pricing', label: 'Pricing & Availability' },
            { key: 'media', label: 'Media' },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key as any)}
              className={`text-sm md:text-base py-2 px-4 border-b-2 transition-all whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-indigo-500 text-indigo-600 font-semibold'
                  : 'border-transparent text-gray-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Forms */}
      {activeTab === 'basic' && (
        <form
          onSubmit={basicForm.handleSubmit((v) => handleSave(v, 'Basic'))}
          className="space-y-4 w-full lg:w-[60%]"
        >
          <Input
            label="House Name"
            className="text-base md:!text-xl"
            {...basicForm.register('houseName')}
            error={basicForm.formState.errors.houseName?.message}
          />
          <Controller
            name="houseType"
            control={basicForm.control}
            render={({ field }) => (
              <Dropdown
                label="House Type"
                options={houseTypes}
                buttonClassName="!w-full !text-xl"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Input
            label="Number of Bedrooms"
            type="number"
            className="text-base md:!text-xl"
            {...basicForm.register('bedrooms')}
            error={basicForm.formState.errors.bedrooms?.message}
          />
          <Input
            label="Number of Bathrooms"
            type="number"
            className="text-base md:!text-xl"
            {...basicForm.register('bathrooms')}
            error={basicForm.formState.errors.bathrooms?.message}
          />
          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={isUpdating}
              className="bg-indigo-600 text-white px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isUpdating && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {isUpdating ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      )}

      {activeTab === 'location' && (
        <form
          onSubmit={locationForm.handleSubmit((v) => handleSave(v, 'Location'))}
          className="space-y-4 w-full lg:w-[60%]"
        >
          <Input
            label="Address"
            className="text-base md:!text-xl"
            {...locationForm.register('address')}
            error={locationForm.formState.errors.address?.message}
          />
          <Controller
            name="city"
            control={locationForm.control}
            render={({ field }) => (
              <Dropdown
                label="City"
                options={cities}
                buttonClassName="!w-full"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="state"
            control={locationForm.control}
            render={({ field }) => (
              <Dropdown
                label="State / Province"
                options={states}
                buttonClassName="!w-full"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Input
            label="ZIP Code"
            type="number"
            className="text-base md:!text-xl"
            {...locationForm.register('zip')}
            error={locationForm.formState.errors.zip?.message}
          />
          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={isUpdating}
              className="bg-indigo-600 text-white px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isUpdating && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {isUpdating ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      )}

      {activeTab === 'specs' && (
        <form
          onSubmit={specsForm.handleSubmit((v) =>
            handleSave(v, 'Specifications'),
          )}
          className="space-y-4 w-full lg:w-[60%]"
        >
          <Input
            label="Area"
            type="number"
            className="text-base md:!text-xl"
            {...specsForm.register('area')}
            error={specsForm.formState.errors.area?.message}
          />
          <Controller
            name="size"
            control={specsForm.control}
            render={({ field }) => (
              <Dropdown
                label="Size Unit"
                options={sizeUnits}
                buttonClassName="!w-full"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="furnished"
            control={specsForm.control}
            render={({ field }) => (
              <Dropdown
                label="Furnished Status"
                options={furnishedOptions}
                buttonClassName="!w-full"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Input
            label="Built Year"
            type="number"
            className="text-base md:!text-xl"
            {...specsForm.register('builtYear')}
            error={specsForm.formState.errors.builtYear?.message}
          />
          <div className="flex items-center justify-between">
            <span className="text-text-primary">Parking Available</span>
            <Switch
              checked={specsForm.watch('parkingAvailable')}
              onChange={(val) => specsForm.setValue('parkingAvailable', val)}
              className={`${
                specsForm.watch('parkingAvailable')
                  ? 'bg-indigo-500'
                  : 'bg-gray-300'
              } relative inline-flex h-6 w-11 items-center rounded-full`}
            >
              <span
                className={`${
                  specsForm.watch('parkingAvailable')
                    ? 'translate-x-6'
                    : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition`}
              />
            </Switch>
          </div>
          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={isUpdating}
              className="bg-indigo-600 text-white px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isUpdating && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {isUpdating ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      )}

      {activeTab === 'pricing' && (
        <form
          onSubmit={pricingForm.handleSubmit((v) => handleSave(v, 'Pricing'))}
          className="space-y-4 w-full lg:w-[60%]"
        >
          <Input
            label="Price"
            type="number"
            className="text-base md:!text-xl"
            {...pricingForm.register('price')}
            error={pricingForm.formState.errors.price?.message}
          />
          <Controller
            name="currency"
            control={pricingForm.control}
            render={({ field }) => (
              <Dropdown
                label="Currency"
                options={currencyOptions}
                buttonClassName="!w-full"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="availability"
            control={pricingForm.control}
            render={({ field }) => (
              <Dropdown
                label="Availability"
                options={availabilityOptions}
                buttonClassName="!w-full"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Input
            label="Listing Date"
            type="date"
            className="text-base md:!text-xl"
            {...pricingForm.register('listingDate')}
            error={pricingForm.formState.errors.listingDate?.message}
          />
          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={isUpdating}
              className="bg-indigo-600 text-white px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isUpdating && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {isUpdating ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      )}

      {activeTab === 'media' && (
        <>
          {/* Upload Section */}
          <div className="mb-8">
            <label className="block text-text-primary mb-2 font-semibold">
              Upload New Images
            </label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 py-16 text-center cursor-pointer hover:border-indigo-400 transition-colors">
                <div className="flex flex-col gap-4 items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 13.5V3"
                      stroke="#141414"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20.25 13.5V19.5H3.75V13.5"
                      stroke="#141414"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8.25 6.75L12 3L15.75 6.75"
                      stroke="#141414"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p className="text-sm">Click to choose files or drag here</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Max 10 images. Size limit: 10MB each
                  </p>
                </div>
              </div>
            </label>

            {/* Selected Files Preview */}
            {selectedFiles.length > 0 && (
              <div className="mt-4 space-y-3">
                <h4 className="font-semibold text-text-primary">
                  Selected Files ({selectedFiles.length})
                </h4>
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded overflow-hidden bg-gray-200">
                        <img
                          src={URL.createObjectURL(file)}
                          alt="preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium truncate max-w-[140px] sm:max-w-[200px]">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>

                    {uploadStatuses[file.name]?.status === 'Success' ? (
                      <span className="text-green-600 text-sm font-medium px-3 py-1 bg-green-50 rounded-full">
                        ✓ Uploaded
                      </span>
                    ) : uploadStatuses[file.name]?.status === 'Error' ? (
                      <span className="text-red-600 text-sm font-medium px-3 py-1 bg-red-50 rounded-full">
                        ✗ Failed
                      </span>
                    ) : (
                      <Button
                        variant={
                          uploadStatuses[file.name]?.status === 'Uploading'
                            ? 'secondary'
                            : 'primary'
                        }
                        size="sm"
                        type="button"
                        disabled={
                          uploadStatuses[file.name]?.status === 'Uploading'
                        }
                        onClick={() => handleUpload(file)}
                      >
                        {uploadStatuses[file.name]?.status === 'Uploading'
                          ? 'Uploading...'
                          : 'Upload'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Existing Images Gallery */}
          <div>
            <h3 className="text-base md:text-2xl font-bold mb-4 text-text-primary">
              Current Images ({house?.images?.length || 0})
            </h3>
            {house?.images && house.images.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {house.images.map((imagePath, index) => (
                  <div
                    key={index}
                    className="relative h-80 rounded-lg overflow-hidden group"
                  >
                    <img
                      src={imagePath}
                      className="w-full h-full object-cover cursor-pointer"
                      alt={`House image ${index + 1}`}
                      // onError={(e) => {
                        // Fallback to placeholder if image fails to load
                        // (e.target as HTMLImageElement).src =
                          // 'https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg';
                      // }}
                    />
                    {/* Delete overlay */}
                    <div className="absolute inset-0 bg-black/30 bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <button
                        onClick={() => handleDeleteImage(imagePath)}
                        disabled={isUpdating}
                        className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        {isUpdating ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <p className="text-gray-500">
                  No images uploaded yet. Upload some images above to get
                  started.
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export const EditHousePage = () => (
  <HousesErrorBoundary onRetry={() => window.location.reload()}>
    <EditHousePageContent />
  </HousesErrorBoundary>
);
